import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SensorsIcon from '@mui/icons-material/Sensors';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { brandColors, currDataColors } from 'types/colors';

const DROPDOWN_MAIN_COLOR = '#344767';

const formatPercentage = (value, isAverage = true) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';

  const rounded = Math.round(number * 10) / 10;
  return `${isAverage ? '~' : ''}${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}`;
};

const getLowestMetric = scope =>
  [
    { field: 'co2_ratio_min', label: 'Ventilation level' },
    { field: 'clean_air_level_min', label: 'Clean air level' },
  ].reduce((lowestMetric, metric) => {
    const value = Number(scope[metric.field]);
    return Number.isFinite(value) && (lowestMetric === null || value < lowestMetric.value) ? { ...metric, value } : lowestMetric;
  }, null);

const BuildingDemandModalContent = ({ aggregates, latest, demandType, category, isRealTime }) => {
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [sortCategory, setSortCategory] = useState('justified');
  const [sortDirection, setSortDirection] = useState('asc');
  const displayedAggregates = isRealTime ? latest : aggregates;
  const aggregateDevices = displayedAggregates?.devices || [];
  const aggregateFloors = displayedAggregates?.floors || [];
  const aggregateAreas = displayedAggregates?.areas || [];
  const metricPrefix = demandType === 'ventilation' ? 'air' : 'thermal';
  const allDemandMetrics = useMemo(
    () => [
      { field: `${metricPrefix}_justified`, label: 'Justified', color: brandColors.BLUE },
      { field: `${metricPrefix}_saveable`, label: 'Saveable', color: brandColors.GREEN },
      { field: `${metricPrefix}_requested`, label: 'Requested', color: brandColors.RED },
    ],
    [metricPrefix],
  );
  const demandMetrics = category ? allDemandMetrics.filter(metric => metric.label.toLowerCase() === category) : allDemandMetrics;
  const sortMetric = allDemandMetrics.find(metric => metric.label.toLowerCase() === sortCategory);
  const sortMultiplier = sortDirection === 'asc' ? 1 : -1;
  useEffect(() => {
    setSelectedFloor(null);
    setSelectedArea(null);
  }, [aggregates, latest, demandType, category]);

  const sortScopes = (scopes, fallback) =>
    [...scopes].sort((firstScope, secondScope) => {
      const firstValue = Number(firstScope[sortMetric.field]);
      const secondValue = Number(secondScope[sortMetric.field]);
      const firstHasValue = Number.isFinite(firstValue);
      const secondHasValue = Number.isFinite(secondValue);

      if (firstHasValue && secondHasValue) return sortMultiplier * (firstValue - secondValue);
      if (firstHasValue) return -1;
      if (secondHasValue) return 1;
      return fallback(firstScope, secondScope);
    });

  const areasForFloor = selectedFloor
    ? aggregateAreas.filter(area => String(area.floor_number) === String(selectedFloor))
    : aggregateAreas;
  const devicesForArea = selectedArea
    ? aggregateDevices.filter(device => String(device.area_id) === String(selectedArea))
    : selectedFloor
      ? aggregateDevices.filter(device => String(device.floor_number) === String(selectedFloor))
      : aggregateDevices;
  const showingFloors = !selectedFloor && aggregateFloors.length > 1;
  const showingAreas = !showingFloors && !selectedArea && areasForFloor.length > 1;
  const scopes = showingFloors
    ? sortScopes(aggregateFloors, (first, second) => Number(first.floor_number) - Number(second.floor_number))
    : showingAreas
      ? sortScopes(areasForFloor, (first, second) => String(first.area_name).localeCompare(String(second.area_name)))
      : sortScopes(devicesForArea, () => 0);
  const scopeType = showingFloors ? 'floor' : showingAreas ? 'area' : 'device';
  const isAverageValue = !isRealTime || scopeType !== 'device';
  const viewKey = selectedArea
    ? `area-${selectedArea}`
    : showingAreas
      ? `floor-${selectedFloor}`
      : showingFloors
        ? 'floors'
        : 'devices';
  const animatedCardSx = index => ({
    border: '1px solid #d2d2d2',
    boxShadow: 'none',
    backgroundColor: '#f9f9f9',
    borderRadius: 2,
    opacity: 0,
    animation: 'buildingDemandCardEnter 280ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
    animationDelay: `${Math.min(index * 45, 225)}ms`,
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1, transition: 'none' },
  });

  const renderDemandBreakdown = scope => {
    const metrics = demandMetrics.map(metric => ({ ...metric, value: Number(scope[metric.field]) }));
    const total = metrics.reduce((sum, metric) => sum + Math.max(Number.isFinite(metric.value) ? metric.value : 0, 0), 0);

    return (
      <Stack spacing={0.75} sx={{ mt: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: 30,
            borderRadius: 1.5,
            backgroundColor: brandColors.GREY_LIGHT,
            overflow: 'hidden',
          }}>
          {metrics.map(metric => {
            const percentage = total ? (Math.max(metric.value || 0, 0) / total) * 100 : 0;
            return (
              <Box
                key={metric.field}
                sx={{
                  width: `${percentage}%`,
                  height: '100%',
                  background: metric.color,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'width 350ms ease',
                }}>
                {percentage >= 1.5 && (
                  <Typography component="span" variant="body2" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1 }}>
                    {metric.label[0]}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ width: '100%', justifyContent: 'space-between' }}
          spacing={{ xs: 0.5, sm: 3 }}>
          {metrics.map(metric => (
            <Typography key={metric.field} variant="body2" sx={{ fontWeight: 600, color: metric.color }}>
              {metric.label}: {Number.isFinite(metric.value) ? `${formatPercentage(metric.value, isAverageValue)}%` : '—'}
            </Typography>
          ))}
        </Stack>
      </Stack>
    );
  };

  const dropdownSx = {
    backgroundColor: '#fff',
    color: DROPDOWN_MAIN_COLOR,
    '&& .MuiSelect-select': { color: DROPDOWN_MAIN_COLOR },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DROPDOWN_MAIN_COLOR },
    '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: DROPDOWN_MAIN_COLOR,
      borderWidth: 2,
    },
  };
  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: '#fff !important',
        border: `2px solid ${DROPDOWN_MAIN_COLOR}`,
        borderTop: 0,
        borderRadius: '0 0 4px 4px',
        mt: '-6px',
      },
    },
  };
  const menuItemSx = {
    '&&': { color: DROPDOWN_MAIN_COLOR },
    '&&:hover': { backgroundColor: '#f2f2f2', color: DROPDOWN_MAIN_COLOR },
    '&&.Mui-selected, &&.Mui-selected:hover': { backgroundColor: DROPDOWN_MAIN_COLOR, color: '#fff' },
  };
  const sortControls = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0} gap={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
      <FormControl sx={{ width: 150 }}>
        <InputLabel sx={{ color: DROPDOWN_MAIN_COLOR, '&.Mui-focused': { color: DROPDOWN_MAIN_COLOR } }}>Metric</InputLabel>
        <Select
          value={sortCategory}
          label="Metric"
          onChange={event => setSortCategory(event.target.value)}
          sx={dropdownSx}
          MenuProps={menuProps}>
          {[
            { value: 'justified', label: 'Justified' },
            { value: 'saveable', label: 'Saveable' },
            { value: 'requested', label: 'Requested' },
          ].map(option => (
            <MenuItem key={option.value} value={option.value} sx={menuItemSx}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ width: 150 }}>
        <InputLabel sx={{ color: DROPDOWN_MAIN_COLOR, '&.Mui-focused': { color: DROPDOWN_MAIN_COLOR } }}>Order</InputLabel>
        <Select
          value={sortDirection}
          label="Order"
          onChange={event => setSortDirection(event.target.value)}
          sx={dropdownSx}
          MenuProps={menuProps}>
          {[
            { value: 'asc', label: '▲Ascending' },
            { value: 'desc', label: '▼Descending' },
          ].map(option => (
            <MenuItem key={option.value} value={option.value} sx={menuItemSx}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  const renderHeader = () => {
    if (showingFloors) return <Stack alignItems={{ sm: 'center' }}>{sortControls}</Stack>;
    if (showingAreas) {
      const floorNumber = selectedFloor ?? areasForFloor[0]?.floor_number;

      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            gap: 1,
            alignItems: 'center',
            width: '100%',
          }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828', justifySelf: { md: 'start' } }}>
            Floor {floorNumber}
          </Typography>
          <Box sx={{ justifySelf: { md: 'center' } }}>{sortControls}</Box>
          <Button
            size="large"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => setSelectedFloor(null)}
            sx={{
              justifySelf: { md: 'end' },
              pr: 1.5,
              color: '#0d142b',
            }}>
            Back to floors
          </Button>
        </Box>
      );
    }
    if (selectedArea) {
      const area = areasForFloor.find(item => String(item.area_id) === String(selectedArea));
      const floorNumber = selectedFloor ?? area?.floor_number;

      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            gap: 1,
            alignItems: 'center',
            width: '100%',
          }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828', justifySelf: { md: 'start' } }}>
            Floor {floorNumber} • Area {area?.area_name || 'Unassigned'}
          </Typography>
          <Box sx={{ justifySelf: { md: 'center' } }}>{sortControls}</Box>
          <Button
            size="large"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={() => setSelectedArea(null)}
            sx={{
              justifySelf: { md: 'end' },
              pr: 1.5,
              color: '#0d142b',
            }}>
            Back to areas
          </Button>
        </Box>
      );
    }
    return null;
  };

  return (
    <Stack
      spacing={1.5}
      sx={{
        minWidth: { xs: '100%', sm: 400, md: 900 },
        maxHeight: 'calc(100vh - 180px)',
        overflowY: 'auto',
        p: 2,
        '@keyframes buildingDemandCardEnter': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}>
      <Fade key={viewKey} in timeout={220} appear>
        <Stack spacing={1.5}>
          {renderHeader()}
          {scopes.length ? (
            scopes.map((scope, index) => {
              const canDrillDown =
                (scopeType === 'floor' &&
                  aggregateAreas.some(area => String(area.floor_number) === String(scope.floor_number))) ||
                (scopeType === 'area' && aggregateDevices.some(device => String(device.area_id) === String(scope.area_id)));
              const secondaryMetric = demandType === 'ventilation' ? getLowestMetric(scope) : null;
              const levelValue = Number(demandType === 'ventilation' ? secondaryMetric?.value : scope.thermal_level_avg);
              const levelSx = Number.isFinite(levelValue)
                ? levelValue >= 85
                  ? {
                      backgroundColor: currDataColors.GREEN_LIGHT,
                      borderColor: currDataColors.GREEN_DARK,
                      color: currDataColors.GREEN_DARK,
                    }
                  : levelValue >= 71
                    ? {
                        backgroundColor: currDataColors.YELLOW_LIGHT,
                        borderColor: currDataColors.YELLOW_DARK,
                        color: currDataColors.YELLOW_DARK,
                      }
                    : {
                        backgroundColor: currDataColors.RED_LIGHT,
                        borderColor: currDataColors.RED_DARK,
                        color: currDataColors.RED_DARK,
                      }
                : {
                    backgroundColor: brandColors.GREY_LIGHT,
                    borderColor: brandColors.GREY_DARK,
                    color: brandColors.BLACK,
                  };
              const content = (
                <CardContent sx={{ '&:last-child': { pb: 2 }, transition: 'transform 180ms ease' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2 }}>
                    {scopeType === 'device' && <SensorsIcon fontSize="medium" sx={{ color: '#282828' }} />}
                    <Typography
                      component={scopeType === 'device' ? 'a' : 'div'}
                      href={scopeType === 'device' ? `/data-explorer/${scope.device_id}` : undefined}
                      target={scopeType === 'device' ? '_blank' : undefined}
                      rel={scopeType === 'device' ? 'noopener noreferrer' : undefined}
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: '#282828',
                        textDecoration: 'none',
                        ...(scopeType === 'device' && { '&:hover': { textDecoration: 'underline' } }),
                      }}>
                      {scopeType === 'floor'
                        ? `Floor ${scope.floor_number} • ${scope.area_count || 0} ${(scope.area_count || 0) === 1 ? 'area' : 'areas'}`
                        : scopeType === 'area'
                          ? scope.area_name
                            ? `Area ${scope.area_name}`
                            : 'Unassigned'
                          : scope.device_name || `Sensor ${scope.device_id}`}
                    </Typography>
                    {scopeType !== 'device' && (
                      <Typography variant="body2" sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SensorsIcon fontSize="medium" />
                        Sensors: {scope.device_count}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {demandType === 'ventilation' ? (
                          <>
                            {secondaryMetric?.label || '—'}:{' '}
                            <Box
                              component="span"
                              sx={{
                                ...levelSx,
                                // border: '1px solid',
                                borderRadius: '.4rem',
                                display: 'inline-block',
                                lineHeight: 1,
                                ml: 0.5,
                                px: 0.75,
                                py: 0.4,
                              }}>
                              {secondaryMetric ? `${formatPercentage(secondaryMetric.value, isAverageValue)}%` : '—'}
                            </Box>
                          </>
                        ) : (
                          <>
                            Comfort Level:{' '}
                            <Box
                              component="span"
                              sx={{
                                ...levelSx,
                                // border: '1px solid',
                                borderRadius: '.4rem',
                                display: 'inline-block',
                                lineHeight: 1,
                                ml: 0.5,
                                px: 0.75,
                                py: 0.4,
                              }}>
                              {formatPercentage(scope.thermal_level_avg, isAverageValue)}%
                            </Box>
                          </>
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                  {renderDemandBreakdown(scope)}
                </CardContent>
              );
              return (
                <Card
                  key={`${scopeType}-${scope.device_id ?? scope.area_id ?? scope.floor_number ?? 'unassigned'}`}
                  variant="outlined"
                  elevation={0}
                  sx={animatedCardSx(index)}>
                  {canDrillDown ? (
                    <CardActionArea
                      onClick={() => {
                        if (scopeType === 'floor') {
                          setSelectedFloor(scope.floor_number);
                          setSelectedArea(null);
                        } else {
                          setSelectedArea(scope.area_id);
                        }
                      }}
                      sx={{ transition: 'background-color 180ms ease', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.025)' } }}>
                      {content}
                    </CardActionArea>
                  ) : (
                    content
                  )}
                </Card>
              );
            })
          ) : (
            <Typography align="center" sx={{ color: '#282828' }}>
              No demand data is available for this period.
            </Typography>
          )}
        </Stack>
      </Fade>
    </Stack>
  );
};

BuildingDemandModalContent.propTypes = {
  aggregates: PropTypes.shape({
    devices: PropTypes.arrayOf(PropTypes.object),
    floors: PropTypes.arrayOf(PropTypes.object),
    areas: PropTypes.arrayOf(PropTypes.object),
  }),
  latest: PropTypes.shape({
    devices: PropTypes.arrayOf(PropTypes.object),
    floors: PropTypes.arrayOf(PropTypes.object),
    areas: PropTypes.arrayOf(PropTypes.object),
  }),
  demandType: PropTypes.oneOf(['cooling', 'ventilation']),
  category: PropTypes.oneOf(['justified', 'saveable', 'requested']),
  isRealTime: PropTypes.bool,
};

BuildingDemandModalContent.defaultProps = {
  aggregates: { devices: [], floors: [], areas: [] },
  latest: { devices: [], floors: [], areas: [] },
  selectedTimeRange: undefined,
  demandType: 'cooling',
  category: undefined,
  isRealTime: false,
};

export default BuildingDemandModalContent;
